import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  categoryApi,
  clientApi,
  newsApi,
  partnerApi,
  productApi,
  documentApi,
  bannerApi,
} from '@apis/index';
import { If } from '@components/condition';
import PageLayout from '@layouts/MainLayout';
import { userSelector } from '@store/user/user.slice';
import { ICategory, IClient, INews, IPartner, IProduct, IDocument, IBanner } from '@type/index';
import { IResponse } from '@type/response';
import MasonryGallery from '@features/home/MasonryGallery';

// import trực tiếp
import SectionBanner from '@features/home/SectionBanner';
import SectionService from '@features/home/SectionService';
import { useIsMobile } from '@hooks/useIsMobile';
// dynamic cho phần phụ
const SectionCategory = dynamic(() => import('@features/home/SectionCategory'), { ssr: false });
const SectionProduct = dynamic(() => import('@features/home/SectionProduct'), { ssr: false });
const SectionFeedback = dynamic(() => import('@features/home/SectionFeedback'), { ssr: false });
const SectionNews = dynamic(() => import('@features/home/SectionNews'), { ssr: false });
const SectionPartner = dynamic(() => import('@features/home/SectionPartner'), { ssr: false });
const SectionClient = dynamic(() => import('@features/home/SectionClient'), { ssr: false });
const SectionCertificate = dynamic(() => import('@features/home/SectionCertificate'), {
  ssr: false,
});
const SectionContact = dynamic(() => import('@features/home/SectionContact'), { ssr: false });

interface IProps {
  categories: ICategory[];
  partners: IPartner[];
  news: IResponse<INews>;
  clients: IClient[];
  documents: IDocument[];
  banners?: IBanner[];
}

function HomePage({ categories, partners, news, clients, documents, banners }: IProps) {
  const { isAuth } = useSelector(userSelector);
  const isMobile = useIsMobile();

  const [products, setProducts] = useState<IResponse<IProduct>>(null);

  const handleGetProducts = useCallback(async () => {
    try {
      const { data } = await productApi.getProductRandom({
        limit: isMobile ? 12 : 28,
        page: 1,
      });
      setProducts(data);
    } catch (e) {
      setProducts(null);
    }
  }, [isMobile]);

  useEffect(() => {
    handleGetProducts();
  }, [handleGetProducts, isAuth]);

  return (
    <div className="min-h-screen">
      <SectionBanner />
      <SectionService />
      <SectionClient clients={clients} />
      <SectionPartner partners={partners} />
      <SectionCertificate documents={documents} />
      <SectionCategory
        categories={categories}
        listIntroduction={[
          {
            _id: '1',
            name: 'Tươi mới, Sạch sẽ nhờ sản phẩm của chúng tôi',
            slug: '',
            description: '',
            image: '/img/category/category_1.webp',
          },
          {
            _id: '2',
            name: 'Biến bữa sáng của bạn trở nên lành mạnh và dễ dàng',
            slug: '',
            description: '',
            image: '/img/category/category_2.webp',
          },
          {
            _id: '3',
            name: 'Sản phẩm hữu cơ tốt nhất mua sắm online dễ dàng',
            slug: '',
            description: '',
            image: '/img/category/category_3.webp',
          },
        ]}
      />
      <SectionProduct className="my-10" products={products} />
      <SectionFeedback />
      <If condition={news?.data?.length} Then={<SectionNews news={news?.data} />} />
      <If condition={banners?.length} Then={<MasonryGallery banners={banners} />} />
      <SectionContact />
      {/* <ContactFixed /> */}
    </div>
  );
}

export async function getStaticProps() {
  try {
    const [
      categoryResponse,
      newsResponse,
      partnerResponse,
      bannerResponse,
      clientResponse,
      documentResponse,
    ] = await Promise.all([
      categoryApi.getCategory(),
      newsApi.getNews(),
      partnerApi.getPartner(),
      bannerApi.getBanner(),
      clientApi.getClient(),
      documentApi.getDocument(),
    ]);
    return {
      props: {
        categories: categoryResponse.data || [],
        partners: partnerResponse.data || [],
        clients: clientResponse.data || [],
        news: newsResponse?.data || null,
        documents: documentResponse?.data || null,
        banners: bannerResponse?.data || [],
      },
      revalidate: 30,
    };
  } catch (error) {
    return {
      props: {
        categories: [],
        partners: [],
        banner: [],
        clients: [],
        news: null,
        documents: [],
      },
      revalidate: 30,
    };
  }
}

HomePage.getLayout = function getLayout(page) {
  return (
    <PageLayout
      title="Trang chủ"
      description="Thực phẩm QUẢNG ĐÀ chuyên cung cấp nguyên liệu và sản phẩm cho bếp ăn công nghiệp, bếp trường học, resort, nhà hàng khách sạn."
      path="/"
    >
      {page}
    </PageLayout>
  );
};

export default HomePage;
